import { useState } from 'react';

import { Badge, Button, Card, Modal, Table } from 'react-bootstrap';
import { FaBoxes, FaCheck, FaTrash } from 'react-icons/fa';
import { FaPencil, FaX } from 'react-icons/fa6';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Paginate from '../../components/ui/Paginate';
import Meta from '../../components/layout/Meta';
import {
    useGetAllProductsContificoQuery,
    useUpdateProductContificoMutation,
} from '../../slices/contificoProductApiSlice';
import { useDeleteProductMutation } from '../../slices/productApiSlice';
import { useRecomputeStatsMutation } from '../../slices/productEventsApiSlice';
import useIsMobile from '../../utils/useIsMobile';
import ProductEditScreen from './ProductEditScreen';

const ProductListScreen = () => {
    const isMobile = useIsMobile();
    const { pageNumber, keyword } = useParams();
    const [show, setShow] = useState(false);
    const [pageSize] = useState(15);

    const { activeCompany } = useSelector((state) => state.admin);

    const [selectedProduct, setSelectedProduct] = useState(null);

    const {
        data: products,
        isLoading,
        error,
        refetch,
        isFetching,
    } = useGetAllProductsContificoQuery(
        {
            pageNumber,
            pageSize,
            keyword,
            company: activeCompany?.ruc,
        },
        {
            skip: !activeCompany,
        }
    );

    const [deleteProduct, { isLoading: loadingDelete }] = useDeleteProductMutation();

    const [updateProductContifico] = useUpdateProductContificoMutation();

    const [recomputeStats, { isLoading: loadingRecompute }] = useRecomputeStatsMutation();

    const getHomeSantyDetails = (personalizado1) => {
        if (personalizado1 && typeof personalizado1 === 'string') {
            try {
                const details = JSON.parse(personalizado1);
                if (typeof details !== 'object' || details === null) {
                    return { onlinePvp: false, onlinePvpDist: false, homeSantyDb: false };
                }
                return details;
            } catch (error) {
                return { onlinePvp: false, onlinePvpDist: false, homeSantyDb: false };
            }
        }
        return { onlinePvp: false, onlinePvpDist: false, homeSantyDb: false };
    };

    const recomputeHandler = async () => {
        if (window.confirm('¿Estás seguro de recomputar las estadísticas?')) {
            try {
                await recomputeStats({ days: 30 }).unwrap();
                toast.success('Estadísticas recomputadas con éxito');
            } catch (err) {
                toast.error(err?.data?.message || err.error);
            }
        }
    };

    const performDeletion = async (id) => {
        try {
            //First Mutation
            await deleteProduct({ contificoId: id }).then(async () => {
                const homeSantyDetails = {
                    onlinePvp: false,
                    onlinePvpDist: false,
                    homeSantyDb: false,
                };

                //Second Mutation
                const updateProductContificoResult = await updateProductContifico({
                    productId: id,
                    data: {
                        personalizado1: JSON.stringify(homeSantyDetails),
                    },
                }).unwrap();

                console.log('updateProductContifico result:', updateProductContificoResult);

                refetch();

                toast.success(
                    'Producto ' +
                        updateProductContificoResult.nombre +
                        ' ha sido removido de HomeSanty'
                );
            });
        } catch (err) {
            // If any mutation fails, handle rollback
            console.error('Transaction failed:', error);
            toast.error(err?.data?.message || err.error);
            throw new Error('Transaction failed');
        }
    };

    const deleteHandler = (id) => {
        if (window.confirm('Estas seguro de eliminar el producto de HomeSanty?')) {
            console.log(id);
            performDeletion(id);
        }
    };

    const handleShow = (product) => {
        setSelectedProduct(product);
        setShow(true);
    };

    const handleClose = () => {
        setShow(false);
    };

    return (
        <>
            <Meta title="Lista de Productos" />
            <div className="admin-header">
                <h1 className="admin-title responsive-title">
                    <FaBoxes className="title-icon" /> Productos
                    {products?.count && (
                        <Badge bg="secondary" className="ms-2">
                            {products.count}
                        </Badge>
                    )}
                </h1>
                <Button
                    variant="primary"
                    onClick={recomputeHandler}
                    disabled={loadingRecompute}
                >
                    {loadingRecompute ? 'Recomputando...' : 'Recomputar Estadísticas'}
                </Button>
            </div>

            {loadingDelete && <Loader />}

            {!activeCompany ? (
                <Message variant="info">Por favor seleccione una empresa para ver los productos.</Message>
            ) : isLoading || isFetching ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">
                    {error?.data?.message || error?.error || 'Ocurrió un error inesperado'}
                </Message>
            ) : (
                <>
                    {!isMobile && (
                        <div className="admin-table-container shadow-sm mb-4">
                            <Table hover responsive className="admin-table m-0">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th className="text-start">Nombre</th>
                                        <th>Stock</th>
                                        <th>PVP + IVA</th>
                                        <th>PVP Dist. + IVA</th>
                                        <th>Online</th>
                                        <th>Dist.</th>
                                        <th>DB</th>
                                        <th className="text-center align-middle">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products?.results?.map((product) => {
                                        const homeSantyDetails = getHomeSantyDetails(
                                            product.personalizado1
                                        );
                                        return (
                                            <tr key={product.id}>
                                                <td>
                                                    <Badge bg="light" text="dark">
                                                        {product.codigo}
                                                    </Badge>
                                                </td>
                                                <td className="text-start fw-bold">
                                                    {product.nombre}
                                                </td>
                                                <td>
                                                    <Badge
                                                        bg={
                                                            product.cantidad_stock > 0
                                                                ? 'success'
                                                                : 'danger'
                                                        }
                                                    >
                                                        {product.cantidad_stock}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    $
                                                    {(
                                                        product.pvp1 *
                                                        (1 + product.porcentaje_iva / 100)
                                                    ).toFixed(2)}
                                                </td>
                                                <td>
                                                    $
                                                    {(
                                                        product.pvp4 *
                                                        (1 + product.porcentaje_iva / 100)
                                                    ).toFixed(2)}
                                                </td>
                                                <td>
                                                    {homeSantyDetails.onlinePvp ? (
                                                        <FaCheck className="text-success" />
                                                    ) : (
                                                        <FaX className="text-danger" />
                                                    )}
                                                </td>
                                                <td>
                                                    {homeSantyDetails.onlinePvpDist ? (
                                                        <FaCheck className="text-success" />
                                                    ) : (
                                                        <FaX className="text-danger" />
                                                    )}
                                                </td>
                                                <td>
                                                    {homeSantyDetails.homeSantyDb ? (
                                                        <FaCheck className="text-success" />
                                                    ) : (
                                                        <FaX className="text-danger" />
                                                    )}
                                                </td>
                                                <td className="text-center align-middle">
                                                    <div className="action-buttons d-inline-flex justify-content-center align-items-center gap-2">
                                                        <Button
                                                            variant="outline-primary"
                                                            className="btn-sm"
                                                            onClick={() => handleShow(product)}
                                                            title="Editar"
                                                        >
                                                            <FaPencil />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            className="btn-sm"
                                                            onClick={() => deleteHandler(product.id)}
                                                            disabled={!homeSantyDetails.homeSantyDb}
                                                            title="Eliminar"
                                                        >
                                                            <FaTrash />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                    {isMobile && (
                        <div>
                            {products?.results?.map((product) => {
                                const homeSantyDetails = getHomeSantyDetails(product.personalizado1);
                                const pvp1 = (product.pvp1 * (1 + product.porcentaje_iva / 100)).toFixed(2);
                                const pvp4 = (product.pvp4 * (1 + product.porcentaje_iva / 100)).toFixed(2);
                                return (
                                    <Card
                                        key={product.id}
                                        className="mb-4 border-0 shadow-sm rounded-4"
                                        style={{ background: '#fff', borderRadius: '1.25rem' }}
                                    >
                                        <Card.Body className="p-3">
                                            {/* Product Name & Stock */}
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="fw-bold text-dark" style={{ fontSize: '1.15rem', lineHeight: 1.2 }}>{product.nombre}</span>
                                                <span
                                                    className="ms-2 px-3 py-2 rounded-pill"
                                                    style={{
                                                        fontSize: '1em',
                                                        background: product.cantidad_stock > 0 ? '#e6f4ea' : '#fbeaea',
                                                        color: product.cantidad_stock > 0 ? '#218838' : '#b5430f',
                                                        border: 'none',
                                                        fontWeight: 600,
                                                        minWidth: 64,
                                                        display: 'inline-block',
                                                        textAlign: 'center',
                                                        letterSpacing: '0.01em',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                                    }}
                                                >
                                                    <span style={{color: product.cantidad_stock > 0 ? '#218838' : '#b5430f', background: 'transparent'}}>
                                                        Stock: <span style={{color: product.cantidad_stock > 0 ? '#218838' : '#b5430f'}}>{product.cantidad_stock}</span>
                                                    </span>
                                                </span>
                                            </div>
                                            {/* ID */}
                                            <div className="mb-2">
                                                <span className="text-muted" style={{ fontSize: '0.97em' }}>ID: </span>
                                                <span className="fw-semibold text-dark">{product.codigo}</span>
                                            </div>
                                            {/* Prices */}
                                            <div className="d-flex flex-row justify-content-between align-items-stretch mb-3 gap-2">
                                                <div className="flex-fill rounded-3 px-2 py-2" style={{ background: '#f9ede7' }}>
                                                    <div className="text-muted" style={{ fontSize: '0.93em' }}>PVP + IVA</div>
                                                    <div className="fw-bold" style={{ color: '#b5430f', fontSize: '1.1em' }}>${pvp1}</div>
                                                </div>
                                                <div className="flex-fill rounded-3 px-2 py-2" style={{ background: '#f9ede7' }}>
                                                    <div className="text-muted" style={{ fontSize: '0.93em' }}>PVP Dist. + IVA</div>
                                                    <div className="fw-bold" style={{ color: '#b5430f', fontSize: '1.1em' }}>${pvp4}</div>
                                                </div>
                                            </div>
                                            {/* Statuses */}
                                            <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                                                <div className="flex-fill text-center rounded-3 px-1 py-2" style={{ background: '#f5f5f5' }}>
                                                    <div className="text-muted" style={{ fontSize: '0.9em' }}>Online</div>
                                                    {homeSantyDetails.onlinePvp ? (
                                                        <FaCheck className="text-success" title="Disponible online" style={{ fontSize: '1.2em' }} />
                                                    ) : (
                                                        <FaX className="text-danger" title="No disponible online" style={{ fontSize: '1.2em' }} />
                                                    )}
                                                </div>
                                                <div className="flex-fill text-center rounded-3 px-1 py-2" style={{ background: '#f5f5f5' }}>
                                                    <div className="text-muted" style={{ fontSize: '0.9em' }}>Dist.</div>
                                                    {homeSantyDetails.onlinePvpDist ? (
                                                        <FaCheck className="text-success" title="Disponible para distribuidores" style={{ fontSize: '1.2em' }} />
                                                    ) : (
                                                        <FaX className="text-danger" title="No disponible para distribuidores" style={{ fontSize: '1.2em' }} />
                                                    )}
                                                </div>
                                                <div className="flex-fill text-center rounded-3 px-1 py-2" style={{ background: '#f5f5f5' }}>
                                                    <div className="text-muted" style={{ fontSize: '0.9em' }}>DB</div>
                                                    {homeSantyDetails.homeSantyDb ? (
                                                        <FaCheck className="text-success" title="En HomeSanty DB" style={{ fontSize: '1.2em' }} />
                                                    ) : (
                                                        <FaX className="text-danger" title="No en HomeSanty DB" style={{ fontSize: '1.2em' }} />
                                                    )}
                                                </div>
                                            </div>
                                            {/* Actions */}
                                            <div className="d-flex justify-content-between align-items-center gap-2 mt-3">
                                                <Button
                                                    variant="outline-primary"
                                                    className="rounded-3 shadow-sm hs-btn-edit"
                                                    onClick={() => handleShow(product)}
                                                    title="Editar"
                                                    style={{
                                                        flex: '1 1 0',
                                                        width: '50%',
                                                        minWidth: 0,
                                                        minHeight: 48,
                                                        fontSize: '1.25em',
                                                        padding: '0.75em 0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 8px rgba(181,67,15,0.08)'
                                                    }}
                                                >
                                                    <FaPencil />
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    className="rounded-3 shadow-sm hs-btn-delete"
                                                    onClick={() => deleteHandler(product.id)}
                                                    disabled={!homeSantyDetails.homeSantyDb}
                                                    title="Eliminar"
                                                    style={{
                                                        flex: '1 1 0',
                                                        width: '50%',
                                                        minWidth: 0,
                                                        minHeight: 48,
                                                        fontSize: '1.25em',
                                                        padding: '0.75em 0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 8px rgba(181,67,15,0.08)'
                                                    }}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </div>
                                            {/* Scoped hover styles for action buttons */}
                                            <style>{`
                                                .hs-btn-edit:hover, .hs-btn-edit:focus {
                                                    background: #fbeee7 !important;
                                                    border-color: #b5430f !important;
                                                    color: #b5430f !important;
                                                }
                                                .hs-btn-delete:hover, .hs-btn-delete:focus {
                                                    background: #f9e3d6 !important;
                                                    border-color: #b5430f !important;
                                                    color: #b5430f !important;
                                                }
                                                .hs-btn-edit:active {
                                                    background: #f9e3d6 !important;
                                                }
                                                .hs-btn-delete:active {
                                                    background: #fbeee7 !important;
                                                }
                                            `}</style>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    )}

                    <Paginate
                        page={pageNumber || 1}
                        pages={Math.ceil((products?.count || 0) / pageSize)}
                        isAdmin={true}
                        keyword={keyword ? keyword : ''}
                    />
                    <Modal show={show} onHide={handleClose} size="xl">
                        <Modal.Header closeButton>
                            <Modal.Title>Detalles del producto</Modal.Title>
                        </Modal.Header>
                        <ProductEditScreen
                            cProduct={selectedProduct}
                            setShow={setShow}
                            handleClose={handleClose}
                            refetch={refetch}
                        />
                    </Modal>
                </>
            )}
        </>
    );
};

export default ProductListScreen;
